import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { connect } from "react-redux";
import StatsService from "../../services/statsService";
import { RevCard } from "../ds";
import { formatShortDate, hasStripeAccount, isStripeRevoked } from "../../helpers/functions";
import { isEventPublished } from "../../helpers/eventPublication";

const CHECK_ICON = { ok: 'check', warn: 'priority_high', err: 'close' };

const EventSellChecklist = ({ event, user }) => {
    const { t } = useTranslation();
    const [photos, setPhotos] = useState(undefined);
    const [faces, setFaces] = useState(undefined);

    useEffect(() => {
        let cancelled = false;
        setPhotos(undefined);
        setFaces(undefined);

        if (!event?.id) {
            return undefined;
        }

        StatsService.getNumberOfPhotos(event.id)
            .then(response => { if (!cancelled) setPhotos(response?.data?.Data ?? null); })
            .catch(() => { if (!cancelled) setPhotos(null); });

        StatsService.getNumberOfIndexedFaces(event.id)
            .then(response => { if (!cancelled) setFaces(response?.data?.Data ?? null); })
            .catch(() => { if (!cancelled) setFaces(null); });

        return () => { cancelled = true; };
    }, [event?.id]);

    // El precio va en la moneda del fotógrafo (user.currency: EUR/USD/MXN), nunca «€» fijo.
    const locale = t('locale', { returnObjects: true }) || 'es-ES';
    const priceFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency: user?.currency || 'EUR' });
    const formatPrice = (value) => priceFormatter.format(value);

    const unitPrice = event?.pricingData?.unitPrice;
    const hasPrice = unitPrice !== undefined && unitPrice !== null && unitPrice !== '';

    const dateFrom = formatShortDate(event?.dateFrom, locale);
    const published = isEventPublished(event);

    const photosKnown = photos !== undefined && photos !== null;
    const facesKnown = faces !== undefined && faces !== null;
    const photosTone = photosKnown && photos > 0 ? 'ok' : 'warn';

    let photosDetail = '—';
    if (photosKnown) {
        if (photos === 0) {
            photosDetail = t('event.basicData.checklist.photos.none');
        } else {
            photosDetail = (
                <>
                    <Trans i18nKey="event.basicData.checklist.photos.count" count={photos} components={[<span className="rev-num" key="0" />]} />
                    {' · '}
                    {facesKnown
                        ? <Trans i18nKey="event.basicData.checklist.facesIndexed.count" count={faces} components={[<span className="rev-num" key="0" />]} />
                        : '—'}
                </>
            );
        }
    }

    // La validación de Stripe se lee del usuario (GET /user la calcula en vivo contra Stripe:
    // charges_enabled && payouts_enabled), con la misma regla que el chip de Cuenta → Venta de
    // fotos. `event.stripeAccountValidated` solo lo rellena el backend en el camino de la galería
    // (evento por código); en el panel llega siempre a false.
    const stripeKnown = user?.isStripeAccountActive !== undefined || hasStripeAccount(user) || isStripeRevoked(user);
    const stripeOk = hasStripeAccount(user) && !isStripeRevoked(user) && user?.isStripeAccountActive === true;

    const items = [
        {
            tone: 'ok',
            title: t('event.basicData.checklist.codeDate.title'),
            detail: (
                <>
                    <span className="rev-mono">{event?.code}</span> · <span className="rev-num">{dateFrom}</span>
                </>
            ),
        },
        {
            tone: hasPrice ? 'ok' : 'warn',
            to: `/events/${event?.id}/pricing`,
            title: t('event.basicData.checklist.price.title'),
            detail: hasPrice
                ? <Trans i18nKey="event.basicData.checklist.price.detail" values={{ price: formatPrice(unitPrice) }} components={[<span className="rev-num" key="0" />]} />
                : t('event.basicData.checklist.price.unset'),
        },
        {
            tone: photosTone,
            to: `/events/${event?.id}/photos`,
            title: t('event.basicData.checklist.photos.title'),
            detail: photosDetail,
        },
        {
            tone: stripeOk ? 'ok' : (stripeKnown ? 'err' : 'warn'),
            to: '/account/photo-sale/home',
            title: t('event.basicData.checklist.stripe.title'),
            detail: stripeOk ? t('event.basicData.checklist.stripe.ok') : (stripeKnown ? t('event.basicData.checklist.stripe.err') : '—'),
        },
        {
            // El paso final. Sin enlace: el botón «Terminar subida y activar selfies» está en la cabecera de
            // esta misma página.
            tone: published ? 'ok' : 'warn',
            title: t(published ? 'event.basicData.checklist.publishApp.titleDone' : 'event.basicData.checklist.publishApp.title'),
            detail: published
                ? <span className="rev-num">{formatShortDate(event?.completedDate, locale) || t('event.basicData.checklist.publishApp.done')}</span>
                : t('event.basicData.checklist.publishApp.pending'),
        },
    ];

    return (
        <RevCard title={t('event.basicData.card.checklist.title')}>
            {items.map((item, index) => {
                // Lo que ya está resuelto no lleva a ningún sitio; lo pendiente sí, a donde se arregla.
                const pendiente = item.tone !== 'ok' && item.to && event?.id;
                const Fila = pendiente ? Link : 'div';
                const filaProps = pendiente ? { to: item.to } : {};

                return (
                    <Fila key={index} className={`rev-check${pendiente ? ' rev-check--accion' : ''}`} {...filaProps}>
                        <span className={`rev-check__dot ${DOT_CLASS[item.tone]}`}>
                            <span className="material-icons rev-check__icon">{CHECK_ICON[item.tone]}</span>
                        </span>
                        <div className="rev-check__body">
                            <span className="rev-check__title">{item.title}</span>
                            <span className={`rev-help ${HELP_CLASS[item.tone] || ''}`.trim()}>
                                {item.detail}
                            </span>
                        </div>
                    </Fila>
                );
            })}
        </RevCard>
    );
};

const DOT_CLASS = { ok: 'rev-check__dot--ok', warn: 'rev-check__dot--warn', err: 'rev-check__dot--err' };
const HELP_CLASS = { warn: 'rev-help--warn', err: 'rev-help--err' };

const mapStateToProps = state => ({
    user: state.usersReducer.user,
});

export default connect(mapStateToProps)(EventSellChecklist);
